import React from 'react';
import { Card, CardContent, Typography, Chip, Box, Tooltip } from '@mui/material';
import { Slide } from '../../types/contentSlicing';
import { extractWrappedLines } from '../../utils/textFormatting';

interface SlideCardProps {
  slide: Slide;
  slideNumber: number;
  text: string;
}

const SPECIAL_TYPES = ['title_slide', 'section_title', 'thank_you', 'agenda', 'transition'];

function SlideCard({ slide, slideNumber, text }: SlideCardProps) {
  const isSpecialType = SPECIAL_TYPES.includes(slide.layoutDescription);

  // Use generatedContent if available, otherwise extract from line numbers
  const textPreview = slide.generatedContent || extractWrappedLines(text, slide.rangeStartLine, slide.rangeEndLine);
  const truncatedPreview = textPreview.length > 150 ? textPreview.substring(0, 150) + '...' : textPreview;

  return (
    <Card
      sx={{
        minWidth: 280,
        maxWidth: 320,
        height: 200,
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 6
        }
      }}>
      <CardContent sx={{ flex: 1, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Chip label={`#${slideNumber}`} size="small" color="primary" />
          <Typography variant="caption" color="text.secondary">
            {slide.rangeStartLine !== -1 && slide.rangeEndLine !== -1
              ? `Lines ${slide.rangeStartLine}-${slide.rangeEndLine}`
              : 'Generated'}
          </Typography>
        </Box>

        <Box sx={{ mb: 1 }}>
          {isSpecialType ? (
            <Chip label={slide.layoutDescription} color="secondary" size="small" />
          ) : (
            <Tooltip title={slide.layoutDescription} placement="top">
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }} noWrap>
                {slide.layoutDescription}
              </Typography>
            </Tooltip>
          )}
        </Box>

        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.75rem', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
          {truncatedPreview}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default SlideCard;
